import { Link, useParams, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { SITE_URL, SITE_BRAND } from "@/lib/seo-config";
import { BLOG_POSTS, BLOG_POSTS_LIST } from "@/lib/blog-posts";
import { Clock, Tag, ArrowLeft, ArrowRight } from "lucide-react";

function buildPostSEO(post) {
  return {
    title: `${post.title} · Blog Batisur Connect`,
    description: post.excerpt,
    keywords: post.keywords,
    canonical: `/blog/${post.slug}`,
    ogType: "article",
    schema: [
      {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": post.title,
        "description": post.excerpt,
        "keywords": post.keywords,
        "datePublished": post.date,
        "dateModified": post.date,
        "author": { "@type": "Organization", "name": post.author, "url": SITE_URL },
        "publisher": {
          "@type": "Organization",
          "name": SITE_BRAND,
          "url": SITE_URL,
          "logo": { "@type": "ImageObject", "url": `${SITE_URL}/icons/icon-512.png` },
        },
        "mainEntityOfPage": { "@type": "WebPage", "@id": `${SITE_URL}/blog/${post.slug}` },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Accueil", "item": SITE_URL },
          { "@type": "ListItem", "position": 2, "name": "Blog", "item": `${SITE_URL}/blog` },
          { "@type": "ListItem", "position": 3, "name": post.title, "item": `${SITE_URL}/blog/${post.slug}` },
        ],
      },
    ],
  };
}

// Simple Markdown-to-HTML renderer (handles ##, ###, **, |tables|, links, lists)
function renderMarkdown(md) {
  const lines = md.split("\n");
  const out = [];
  let inList = false;
  let inTable = false;
  let tableRows = [];

  const flushList = () => {
    if (inList) {
      out.push("</ul>");
      inList = false;
    }
  };
  const flushTable = () => {
    if (inTable && tableRows.length > 0) {
      const header = tableRows[0];
      const body = tableRows.slice(2); // skip alignment row
      const th = header.map((c) => `<th style="text-align:left;padding:10px;border-bottom:2px solid #0A0A0B;">${c}</th>`).join("");
      const trs = body.map((row) => `<tr>${row.map((c) => `<td style="padding:10px;border-bottom:1px solid #eee;">${inline(c)}</td>`).join("")}</tr>`).join("");
      out.push(`<div style="overflow-x:auto;margin:20px 0;"><table style="width:100%;border-collapse:collapse;font-size:14px;"><thead><tr>${th}</tr></thead><tbody>${trs}</tbody></table></div>`);
      tableRows = [];
      inTable = false;
    }
  };
  const inline = (s) =>
    s
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color:#FF4D00;font-weight:600;">$1</a>');

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line.trim().startsWith("|")) {
      inTable = true;
      const cells = line.trim().slice(1, -1).split("|").map((c) => c.trim());
      tableRows.push(cells);
      continue;
    } else if (inTable) {
      flushTable();
    }
    if (line.startsWith("### ")) {
      flushList();
      out.push(`<h3 class="display" style="font-size:22px;margin-top:32px;margin-bottom:12px;">${inline(line.slice(4))}</h3>`);
    } else if (line.startsWith("## ")) {
      flushList();
      out.push(`<h2 class="display" style="font-size:28px;margin-top:48px;margin-bottom:16px;border-left:4px solid #FF4D00;padding-left:16px;">${inline(line.slice(3))}</h2>`);
    } else if (line.startsWith("- ")) {
      if (!inList) {
        out.push('<ul style="margin:12px 0 12px 24px;list-style:disc;">');
        inList = true;
      }
      out.push(`<li style="margin-bottom:6px;">${inline(line.slice(2))}</li>`);
    } else if (line.startsWith("> ")) {
      flushList();
      out.push(`<blockquote style="border-left:4px solid #FF4D00;background:#FFF6F0;padding:14px 20px;margin:20px 0;font-style:italic;">${inline(line.slice(2))}</blockquote>`);
    } else if (line.trim() === "") {
      flushList();
    } else {
      flushList();
      out.push(`<p style="line-height:1.7;margin:12px 0;">${inline(line)}</p>`);
    }
  }
  flushList();
  flushTable();
  return out.join("");
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS[slug];

  if (!post) return <Navigate to="/blog" replace />;

  const related = BLOG_POSTS_LIST.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-[#F7F7F9]" data-testid={`blog-post-${slug}`}>
      <SEO cfg={buildPostSEO(post)} />
      <Navbar />

      <section className="bg-[#0A0A0B] text-white border-b-2 border-[#0A0A0B]">
        <div className="max-w-3xl mx-auto px-6 py-14 lg:py-16">
          <nav className="mono text-[11px] uppercase tracking-widest text-zinc-400 mb-5" aria-label="Fil d'ariane">
            <Link to="/" className="hover:text-[#FF4D00]">Accueil</Link>
            <span className="mx-2">/</span>
            <Link to="/blog" className="hover:text-[#FF4D00]">Blog</Link>
            <span className="mx-2">/</span>
            <span className="text-[#FF4D00] truncate">{post.category}</span>
          </nav>
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold flex items-center gap-3">
            <span>// {post.category}</span>
            <span className="text-zinc-500">·</span>
            <span className="text-zinc-400 flex items-center gap-1"><Clock className="w-3 h-3" /> {post.readTime} min de lecture</span>
          </div>
          <h1 className="display text-3xl lg:text-5xl tracking-tighter mt-3 leading-tight">{post.title}</h1>
          <p className="mt-5 text-zinc-300 text-base lg:text-lg">{post.heroSubtitle}</p>
          <div className="mt-6 mono text-[10px] uppercase tracking-widest text-zinc-500">
            Publié le {new Date(post.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })} par {post.author}
          </div>
        </div>
      </section>

      <article className="max-w-3xl mx-auto px-6 py-12">
        <div
          className="prose prose-zinc max-w-none"
          data-testid={`blog-content-${slug}`}
          dangerouslySetInnerHTML={{ __html: renderMarkdown(post.body) }}
        />

        <div className="mt-12 pt-8 border-t-2 border-[#0A0A0B]">
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold mb-3">// Tags</div>
          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag) => (
              <span key={tag} className="mono text-[10px] uppercase tracking-widest border border-[#0A0A0B] px-3 py-1 bg-white">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </article>

      {/* Related posts */}
      <section className="bg-white border-t-2 border-[#0A0A0B]">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// À lire aussi</div>
          <h2 className="display text-2xl lg:text-3xl tracking-tighter mt-3">Articles connexes</h2>
          <div className="mt-8 grid md:grid-cols-3 gap-5">
            {related.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="group border-2 border-[#0A0A0B] bg-white p-5 hover:bg-[#FF4D00] hover:text-white transition block"
              >
                <div className="mono text-[10px] uppercase tracking-widest opacity-70">// {p.category}</div>
                <div className="display text-lg mt-2 leading-tight">{p.title}</div>
                <div className="mt-4 flex items-center gap-1 text-sm font-semibold">
                  Lire <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition" />
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/blog" className="mono text-xs uppercase tracking-widest text-[#FF4D00] hover:underline flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" /> Retour au blog
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
